import React, { memo } from 'react';
import { Shift } from 'domain/Shift';
import { Popover } from '@patternfly/react-core';
import { Indictments } from 'ui/pages/shift/ShiftEvent';
import { EditIcon, TrashIcon, CopyIcon, WarningTriangleIcon } from '@patternfly/react-icons';

/* =========================
 * Tipos
 * ========================= */

export interface CellAssignment {
  id: number;
  shift: Shift;
  startTime: string;
  endTime: string;
  employeeName?: string;
  color: 'gray' | 'yellow' | 'red' | 'orange' ;
}

export interface ScheduleCellProps {
  spotId: number | string;
  dateISO: string;
  assignments: CellAssignment[];
  rowHeight: number;
  onAddShift?: (spotId: number | string, dateISO: string) => void;
  onEditShift?: (shiftId: number) => void;
  onDeleteShift?: (shiftId: number) => void;
  onCopyShift?: (shiftId: number) => void;
}

/* =========================
 * Component
 * ========================= */

function ScheduleCell({
  spotId,
  dateISO,
  assignments,
  rowHeight,
  onAddShift,
  onEditShift,
  onDeleteShift,
  onCopyShift,
}: ScheduleCellProps) {
  const hasViolations = (shift: Shift) => {
    return [
      shift.requiredSkillViolationList,
      shift.unavailableEmployeeViolationList,
      shift.shiftEmployeeConflictList,
      shift.rotationViolationPenaltyList,
      shift.contractMinutesViolationPenaltyList,
      shift.noBreakViolationList,
      shift.restOneDayViolationList,
      shift.balanceEmployeeViolationList,
      shift.differentEmployeeDayShiftsViolationList,
      shift.bloquedViolationPenaltyList,
    ].some(list => (list?.length ?? 0) > 0);
  };

  return (
    <div
      className="schCell"
      style={{ height: rowHeight }}
      onDoubleClick={() => {
        onAddShift?.(spotId, dateISO);
      }}
      data-spot={spotId}
      data-date={dateISO}
    >
      {assignments.length === 0 ? (
        <div className="emptyHint">—</div>
      ) : (
        <div className="cellStack">
          {assignments.map((a) => (
            <div
              key={a.id}
              className={`shiftCard shift-${a.color}`}
              title={
                a.employeeName
                  ? `Empleado: ${a.employeeName}`
                  : 'Sin empleado asignado'
              }
            >
              <div className="shiftHeader">
                <div className="shiftTime">
                  {a.startTime}–{a.endTime}
                </div>
                {hasViolations(a.shift) && (
                  <Popover
                    headerContent="Penalizaciones"
                    bodyContent={
                      <div style={{ maxHeight: '250px', overflowY: 'auto'}}>
                        <Indictments {...a.shift} />
                      </div>
                    }
                  >
                    <span className="btnShift" style={{ cursor: 'pointer', marginTop: '3px' }}><WarningTriangleIcon style={{ color: 'red'}}/></span>
                  </Popover>
                )}
                {a.shift.originalEmployee == null && (
                  <div className="shiftActions">
                    <button
                      className="btnShift"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditShift?.(a.id);
                      }}
                      title="Editar turno"
                    >
                      <EditIcon />
                    </button>
                    <button
                      className="btnShift"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteShift?.(a.id);
                      }}
                      title="Eliminar turno"
                    >
                      <TrashIcon />
                    </button>
                    <button
                      className="btnShift"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopyShift?.(a.id);
                      }}
                      title="Copiar turno"
                    >
                      <CopyIcon />
                    </button>
                  </div>
                )}
              </div>

              <div className="shiftMeta">
                {a.employeeName ? (
                  <span className="pill">{a.employeeName}</span>
                ) : (
                  <span className="pill pill--warning">Sin asignar</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default memo(ScheduleCell);
